import { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { toneTextColour } from "../../../polish/LabelValueRow";
import {
  LOAD_EASE_CSS,
  TABULAR,
  type TonalTokens,
} from "../../../polish/tokens";
import { signedPercent, type MetricComparison } from "./feedModel";

// The tweet card's comparison, card-sized: the estimate's bar in the quiet
// tone beside the actual's in its result's colour, values on their ends and
// one zero line. Plain boxes, not SVG, so the labels stay crisp at any width.

/** The plot's fixed height, so two charts side by side keep one baseline. */
export const PLOT_HEIGHT = 116;
/** Room for a value label beyond a bar's end. */
const LABEL_ROOM = 20;
/** The zero line's inset when every bar hangs below it. */
const TOP_INSET = 4;
/**
 * The plot's widest: a narrow chart uses its whole width as before; a wide
 * one (stacked charts) keeps the two bars and their zero line together in
 * the middle instead of stretching across the card.
 */
const PLOT_MAX_WIDTH = 320;

interface Bar {
  key: "estimate" | "actual";
  label: string;
  value: number | null;
  text: string;
  fill: string;
  textColour: string;
}

interface MetricBarsProps {
  metric: MetricComparison;
  tokens: TonalTokens;
  isDark: boolean;
  reduced: boolean;
  /**
   * From md the charts sit one above the other, each with room beside its
   * bars: the heading stays at the top left. Side by side it is centred over
   * its bars as their title.
   */
  stackedFromMd?: boolean;
}

/** One metric: its label and surprise together as its title, then the estimate and actual bars. */
export const MetricBars = ({
  metric,
  tokens,
  isDark,
  reduced,
  stackedFromMd = false,
}: MetricBarsProps) => {
  const frame = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduced);

  // The bars rise once, the first time the plot is on screen.
  useEffect(() => {
    if (shown) return undefined;
    const node = frame.current;
    if (node === null || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setShown(true);
      },
      { threshold: 0.3 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [shown]);

  const actualFill =
    metric.tone === "gain"
      ? tokens.gain
      : metric.tone === "loss"
        ? tokens.loss
        : alpha(tokens.text, isDark ? 0.5 : 0.42);
  const bars: Bar[] = [
    {
      key: "estimate",
      label: "Estimate",
      value: metric.estimated,
      text: metric.estimatedText ?? "–",
      fill: alpha(tokens.text, isDark ? 0.22 : 0.16),
      textColour: tokens.muted,
    },
    {
      key: "actual",
      label: "Actual",
      value: metric.actual,
      text: metric.actualText,
      fill: actualFill,
      textColour: toneTextColour(metric.tone, tokens),
    },
  ];

  const values = bars
    .map((bar) => bar.value)
    .filter((value): value is number => value !== null);
  const high = Math.max(0, ...values);
  const low = Math.min(0, ...values);
  const above = high > 0;
  const below = low < 0;
  const plotTop = above ? LABEL_ROOM : TOP_INSET;
  const plot = PLOT_HEIGHT - plotTop - (below ? LABEL_ROOM : 0);
  const range = high - low;
  const zeroY = range === 0 ? plotTop + plot : plotTop + (plot * high) / range;

  const heading =
    metric.currency === null
      ? metric.label
      : `${metric.label} · ${metric.currency}`;
  const description = `${heading} ${metric.actualText}${
    metric.estimatedText === null ? "" : ` vs estimate ${metric.estimatedText}`
  }`;

  return (
    <Box data-testid={`metric-${metric.key}`} sx={{ minWidth: 0 }}>
      <Box
        data-testid={`metric-${metric.key}-heading`}
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: {
            xs: "center",
            md: stackedFromMd ? "flex-start" : "center",
          },
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "0.8125rem", md: "0.875rem" },
            fontWeight: 500,
            color: tokens.text,
          }}
        >
          {heading}
        </Typography>
        {metric.surprise !== null && (
          <Typography
            data-testid={`metric-${metric.key}-surprise`}
            sx={{
              ...TABULAR,
              fontSize: { xs: "0.8125rem", md: "0.875rem" },
              fontWeight: 500,
              color: toneTextColour(metric.tone, tokens),
            }}
          >
            {signedPercent(metric.surprise)}
          </Typography>
        )}
      </Box>
      <Box
        ref={frame}
        role="img"
        aria-label={description}
        sx={{
          position: "relative",
          height: PLOT_HEIGHT,
          maxWidth: PLOT_MAX_WIDTH,
          mx: "auto",
          mt: 0.5,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: zeroY,
            height: "1px",
            backgroundColor: alpha(tokens.text, 0.1),
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
          }}
        >
          {bars.map((bar, index) => {
            const size =
              bar.value === null || range === 0
                ? 0
                : Math.max((plot * Math.abs(bar.value)) / range, 2);
            // A missing estimate's "–" sits on the side the other bar leaves free.
            const up = bar.value === null ? !below : bar.value >= 0;
            const top = up ? zeroY - size : zeroY;
            const labelTop = up ? top - 18 : top + size + 2;
            return (
              <Box
                key={bar.key}
                aria-hidden
                sx={{ position: "relative", flex: 1, minWidth: 0 }}
              >
                {size > 0 && (
                  <Box
                    data-testid={`metric-${metric.key}-${bar.key}-bar`}
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top,
                      height: size,
                      width: "clamp(28px, 46%, 76px)",
                      backgroundColor: bar.fill,
                      borderRadius: up ? "4px 4px 0 0" : "0 0 4px 4px",
                      transformOrigin: up ? "bottom" : "top",
                      transform: `translateX(-50%) scaleY(${shown ? 1 : 0})`,
                      transition: reduced
                        ? "none"
                        : `transform 600ms ${LOAD_EASE_CSS} ${index * 80}ms`,
                    }}
                  />
                )}
                <Typography
                  data-testid={`metric-${metric.key}-${bar.key}`}
                  sx={{
                    ...TABULAR,
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: labelTop,
                    textAlign: "center",
                    fontSize: { xs: "0.8125rem", md: "0.875rem" },
                    lineHeight: "16px",
                    fontWeight: bar.key === "actual" ? 600 : 500,
                    color: bar.textColour,
                    whiteSpace: "nowrap",
                  }}
                >
                  {bar.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        aria-hidden
        sx={{ display: "flex", maxWidth: PLOT_MAX_WIDTH, mx: "auto", mt: 0.75 }}
      >
        {bars.map((bar) => (
          <Typography
            key={bar.key}
            sx={{
              flex: 1,
              textAlign: "center",
              fontSize: { xs: "0.6875rem", md: "0.75rem" },
              color: tokens.faint,
            }}
          >
            {bar.label}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};
